import { EventEmitter } from "events";
import * as $ from "jquery";
import StoreContainer from "./StoreContainer";
import { logInfo } from "../helpers/eventLogger";
import EngagementService from "../services/EngagementService";
import CookieStore from "./CookieStore";
import isMobile from "../helpers/isMobile";
import { log } from "console";

class FrameStore {
  private _isPopOut = false;
  private _popoutChatRequestedFromUrl: string;
  private _launcherVisibility = "visible";
  private _isExpanded: boolean;
  private _emitter = new EventEmitter();
  private _popoutWindow: Window;
  private _cookieStore: CookieStore;

  constructor(cookieStore: CookieStore) {
    this._cookieStore = cookieStore;
    this._isExpanded = this._cookieStore.get("isExpanded") || false;
  }

  isExpanded() {
    return this._isExpanded;
  }

  isPopOut() {
    return this._isPopOut;
  }

  async expandOrPopout() {
    if (StoreContainer.SettingStore.areCookiesDisabled()) {
      await this.popout();
    } else {
      await this.expand();
    }
  }

  async expand() {
    const persist = !isMobile();
    this._isExpanded = true;

    if (persist) {
      this._cookieStore.set("isExpanded", true);
    }

    this._emitter.emit("expand");
    logInfo("expanded inline");
  }

  async collapse() {
    await StoreContainer.BootStore.waitForBoot();
    this._isExpanded = false;
    this._cookieStore.set("isExpanded", false);
    this._emitter.emit("collapse");

    if (
      StoreContainer.EngagementStore.hasEngagementId() &&
      StoreContainer.ChatStore.isChatting()
    ) {
      EngagementService.sendInlineMinimized(
        StoreContainer.SettingStore.getSiteId(),
        {
          EngagementId: StoreContainer.EngagementStore.getEngagementId(),
          VisitorId: StoreContainer.VisitorStore.getVisitorId()
        }
      );
    } else if (!StoreContainer.CallStore.hasCallCookie()) {
      StoreContainer.EngagementStore.setEngagementGroupId(undefined);
    }

    logInfo("collapsed inline");
  }

  async popout() {
    await StoreContainer.BootStore.waitForBoot();
    if (this._popoutWindow && !this._popoutWindow.closed) {
      this._popoutWindow.focus();
      return;
    }

    this._emitter.emit("popout"); // I don't think this is used
    this._popoutWindow = window.open(
      this.buildPopoutUrl(),
      "_blank",
      "toolbar=no,menubar=no,location=no,resizable=yes,width=460,height=700"
    );
    this.collapse();
    logInfo("popout");
  }

  async popin() {
    await StoreContainer.BootStore.waitForBoot();
    await StoreContainer.ChatStore.loadMessages();
    this._emitter.emit("popin");
    logInfo("popin");
  }

  onExpand(callback: () => void) {
    this._emitter.addListener("expand", callback);
  }

  onCollapse(callback: () => void) {
    this._emitter.addListener("collapse", callback);
  }

  onFailToCloseWindow(callback: () => void) {
    this._emitter.addListener("closeWindow", callback);
  }

  setLauncherVisibility(visibility: string) {
    if (visibility !== "visible" && visibility !== "hidden") {
      console.warn(
        "setLauncherVisibility param should be 'visible' or 'hidden'"
      );
      return;
    }

    this._launcherVisibility = visibility;
    this._emitter.emit("launcherVisibilityUpdate", visibility);
  }

  preventOpeningOnReload() {
    this._cookieStore.set("isExpanded", false);
  }

  getLauncherVisibility() {
    return this._launcherVisibility;
  }

  onLauncherVisibilityUpdate(callback: (visibility: string) => void) {
    this._emitter.addListener("launcherVisibilityUpdate", callback);
  }

  collapseOrClose() {
    if (this._isPopOut) {
      window.parent.close();
      this._emitter.emit("closeWindow");
    } else {
      this.collapse();
    }
  }

  removeAllListeners() {
    this._emitter.removeAllListeners();
  }

  setIsPopout(isPopout: boolean) {
    this._isPopOut = isPopout;
  }

  setChatRequestedFromUrl(url: string) {
    this._popoutChatRequestedFromUrl = url;
  }

  getChatRequestedFromUrl() {
    return this._popoutChatRequestedFromUrl;
  }

  removeListener(event: string, callback: () => void) {
    this._emitter.removeListener(event, callback);
  }

  private buildPopoutUrl() {
    const url = `${StoreContainer.SettingStore.getConsoleEndpoint()}/chat`;

    const params: any = {
      siteId: StoreContainer.SettingStore.getSiteId()
    };

    const groupId = StoreContainer.GroupStore.getActiveOrPageGroupId();

    if (groupId) {
      params.groupId = groupId;
    }

    if (StoreContainer.EngagementStore.hasEngagementId()) {
      params.engagementId = StoreContainer.EngagementStore.getEngagementId();
    }

    if (StoreContainer.VisitorStore.hasVisitorId()) {
      params.visitorId = StoreContainer.VisitorStore.getVisitorId();
    }

    if (StoreContainer.SettingStore.isClickToCall()) {
      params.isClickToCall = StoreContainer.SettingStore.getClickToCall();
    }

    const customVars = StoreContainer.SettingStore.getCustomVars();

    if (customVars) {
      params.customVars = customVars;
    }

    params.pageUrl = window.parent.location.href.substring(0, 200);

    if (StoreContainer.SettingStore.areCookiesDisabled()) {
      params.cookiesDisabled = true;
    }

    return `${url}?${$.param(params)}`;
  }
}

export default FrameStore;
